/**
 * Bounds self-healing reloads so a permanent failure cannot spin forever.
 *
 * The cooldown guards that call this (`canAutoReload` in `stale-client-reload`,
 * and `tryReloadOnCloudflareChallenge`) are rate limits, not loop breakers: they
 * only require N seconds between reloads, and every reload refreshes the
 * timestamp. When the underlying failure is permanent rather than a deploy race
 * — a purged CDN asset, a GraphQL backend that is down while the Remix server
 * answers, a challenge that keeps failing — that rate limit paces an infinite
 * loop instead of ending it, and the user cannot leave the page. React Router
 * pushes the history entry only after loaders settle, so `location.reload()`
 * returns them to the page they were already on.
 *
 * The budget is **windowed, not per-tab-lifetime**. A loop reloads within
 * seconds, so its attempts land inside one window and it is cut off after two.
 * Genuinely separate incidents — a deploy on Monday and another on Wednesday in
 * a tab left open all week — fall in different windows and each get the full
 * budget back. Charging a successful recovery against every future one would
 * leave a long-lived tab permanently unable to self-heal, which is worse than
 * the loop this exists to stop.
 *
 * The window is anchored on its **first** spend, not its most recent one. An
 * anchor that moves with each spend slides the window forward and can starve an
 * unrelated later incident: three failures four minutes apart would see the
 * second push the window past the third, refusing a reload the unbudgeted code
 * would have performed.
 */
export const MAX_AUTO_RELOADS_PER_WINDOW = 2;

/**
 * How long a spend counts against the budget. Comfortably longer than a reload
 * loop's period (immediate, or the 10s reload cooldown) and far shorter than the
 * gap between real incidents, so the two are cleanly separated.
 */
export const RELOAD_BUDGET_WINDOW_MS = 5 * 60 * 1000;

export type ReloadBudgetOptions = {
    /** Override the per-window cap. */
    max?: number;
};

/**
 * Spends recorded as `<count>:<epoch ms the window opened>`. A missing or
 * corrupted value starts a fresh window rather than reading as exhausted: the
 * window bounds anything that slips through, so resetting is the safer failure
 * mode than locking recovery off for the life of the tab.
 */
const parseSpend = (raw: string | null): { count: number; at: number } => {
    const [countPart, atPart] = (raw ?? '').split(':');
    const count = Number(countPart);
    const at = Number(atPart);
    if (!Number.isFinite(count) || !Number.isFinite(at)) {
        return { count: 0, at: 0 };
    }
    return { count, at };
};

type BudgetWindow = { spends: number; openedAt: number };

/**
 * The live window for `key`, or null when sessionStorage is unreachable.
 *
 * Both entry points treat null as "no budget". There is no way to count a loop
 * without persistence, so allowing the reload in a sandboxed iframe or a
 * storage-blocked webview would leave exactly one context where the cap does
 * nothing and the page can reload forever. Callers surface a manual retry
 * instead, which costs the user a click and cannot spin.
 */
const readWindow = (key: string, now: number): BudgetWindow | null => {
    try {
        const { count, at } = parseSpend(window.sessionStorage.getItem(key));
        if (now - at >= RELOAD_BUDGET_WINDOW_MS) {
            return { spends: 0, openedAt: now };
        }
        // `openedAt` carries the original anchor forward, so spending inside a
        // window never extends it.
        return { spends: count, openedAt: at };
    } catch {
        return null;
    }
};

/**
 * Whether `key` has budget left, without spending any.
 *
 * Callers that charge another guard (a cooldown timestamp) should check first,
 * so a refused budget doesn't leave that guard charged for a reload that never
 * happened, then `spendReloadBudget` once the reload is certain.
 */
export const hasReloadBudget = (
    key: string,
    options: ReloadBudgetOptions = {},
): boolean => {
    if (typeof window === 'undefined') return false;

    const current = readWindow(key, Date.now());
    if (current === null) return false;
    return current.spends < (options.max ?? MAX_AUTO_RELOADS_PER_WINDOW);
};

/**
 * Claim one reload against `key`'s budget for the current window.
 *
 * @returns true if the caller may reload.
 */
export const spendReloadBudget = (
    key: string,
    options: ReloadBudgetOptions = {},
): boolean => {
    if (typeof window === 'undefined') return false;

    const now = Date.now();
    const current = readWindow(key, now);
    if (current === null) return false;
    if (current.spends >= (options.max ?? MAX_AUTO_RELOADS_PER_WINDOW)) {
        return false;
    }

    try {
        window.sessionStorage.setItem(
            key,
            `${current.spends + 1}:${current.openedAt}`,
        );
    } catch {
        return false;
    }
    return true;
};
